// ============================================================================
// RISK MATRIX TYPES
// ============================================================================

/**
 * Risk probability levels (1-5)
 * 1 = Very Low, 2 = Low, 3 = Medium, 4 = High, 5 = Very High
 */
export type RiskProbability = 1 | 2 | 3 | 4 | 5;

/**
 * Risk impact levels (1-5)
 * 1 = Very Low, 2 = Low, 3 = Medium, 4 = High, 5 = Very High
 */
export type RiskImpact = 1 | 2 | 3 | 4 | 5;

/**
 * Risk categories
 */
export type RiskCategory =
  | 'technical'      // Technical risks (tech debt, complexity, etc.)
  | 'financial'      // Financial risks (budget, funding, etc.)
  | 'organizational' // Organizational risks (resources, team, etc.)
  | 'external'       // External risks (market, legal, etc.)
  | 'quality'        // Quality risks (defects, performance, etc.)
  | 'schedule';      // Schedule risks (delays, dependencies, etc.)

/**
 * Risk status in the risk management process
 */
export type RiskStatus =
  | 'identified'   // Risk has been identified
  | 'analyzing'    // Risk is being analyzed
  | 'mitigating'   // Mitigation plan is being executed
  | 'monitoring'   // Risk is being actively monitored
  | 'closed'       // Risk has been closed (occurred or no longer relevant)
  | 'accepted';    // Risk has been accepted (no mitigation)

/**
 * Risk level based on probability and impact
 */
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

/**
 * Risk response strategies
 */
export type RiskResponse =
  | 'avoid'      // Eliminate the risk
  | 'mitigate'   // Reduce probability or impact
  | 'transfer'   // Transfer risk to third party
  | 'accept';    // Accept the risk

/**
 * Individual risk item
 */
export interface Risk {
  id: string;
  title: string;
  description: string;
  category: RiskCategory;

  // Risk assessment
  probability: RiskProbability;
  impact: RiskImpact;
  score: number;              // probability × impact (1-25)
  level: RiskLevel;           // Calculated risk level

  // Risk management
  status: RiskStatus;
  response: RiskResponse;
  owner?: string;             // Member ID responsible for the risk

  // Action plans
  mitigationPlan?: string;    // How to reduce probability
  contingencyPlan?: string;   // What to do if risk occurs
  triggerConditions?: string; // Conditions that trigger the risk

  // Tracking
  identifiedDate: Date;
  lastReviewDate?: Date;
  targetResolutionDate?: Date;
  closedDate?: Date;

  // Additional context
  relatedTasks?: string[];    // IDs of related PERT tasks
  relatedRisks?: string[];    // IDs of related risks
  tags: string[];
  attachments?: string[];

  // History
  notes?: RiskNote[];
  historyLog?: RiskHistoryEntry[];
}

/**
 * Note on a risk
 */
export interface RiskNote {
  id: string;
  riskId: string;
  author?: string;
  content: string;
  createdAt: Date;
}

/**
 * History entry for risk changes
 */
export interface RiskHistoryEntry {
  id: string;
  riskId: string;
  timestamp: Date;
  action: 'created' | 'updated' | 'status_changed' | 'reassigned' | 'closed';
  field?: string;
  oldValue?: string;
  newValue?: string;
  author?: string;
  comment?: string;
}

/**
 * Risk Matrix containing all risks
 */
export interface RiskMatrix {
  id: string;
  projectId: string;
  risks: Risk[];

  // Statistics
  totalRisks: number;
  criticalRisks: number;
  highRisks: number;
  mediumRisks: number;
  lowRisks: number;

  // By status
  identifiedRisks: number;
  mitigatingRisks: number;
  monitoringRisks: number;
  closedRisks: number;

  // Metadata
  lastUpdated: Date;
  createdAt: Date;
}

/**
 * Risk matrix configuration
 */
export interface RiskMatrixConfig {
  // Matrix dimensions (3x3 or 5x5)
  matrixSize: 3 | 5;

  // Level thresholds (for 5x5 matrix)
  // Score = probability × impact
  lowThreshold: number;       // <= this score = low risk
  mediumThreshold: number;    // <= this score = medium risk
  highThreshold: number;      // <= this score = high risk
  // > highThreshold = critical risk

  // Display settings
  showClosedRisks: boolean;
  defaultView: 'matrix' | 'list' | 'kanban';
  sortBy: 'score' | 'date' | 'category' | 'status';
  groupBy: 'category' | 'status' | 'owner' | 'level' | 'none';

  // Notifications
  notifyOnNewRisk: boolean;
  notifyOnCriticalRisk: boolean;
  notifyBeforeReviewDate: number; // Days before review date

  // Filters
  filters: {
    categories: RiskCategory[];
    statuses: RiskStatus[];
    levels: RiskLevel[];
    searchQuery: string;
    showOnlyAssigned: boolean;
    showOnlyActive: boolean;
  };
}

/**
 * Default configuration for risk matrix
 */
export const DEFAULT_RISK_MATRIX_CONFIG: RiskMatrixConfig = {
  matrixSize: 5,
  lowThreshold: 6,      // 1-6 = low
  mediumThreshold: 12,  // 7-12 = medium
  highThreshold: 18,    // 13-18 = high
                        // 19-25 = critical
  showClosedRisks: false,
  defaultView: 'matrix',
  sortBy: 'score',
  groupBy: 'none',
  notifyOnNewRisk: true,
  notifyOnCriticalRisk: true,
  notifyBeforeReviewDate: 7,
  filters: {
    categories: [],
    statuses: [],
    levels: [],
    searchQuery: '',
    showOnlyAssigned: false,
    showOnlyActive: true,
  },
};

/**
 * Risk filter options
 */
export interface RiskFilter {
  categories?: RiskCategory[];
  statuses?: RiskStatus[];
  levels?: RiskLevel[];
  owners?: string[];
  tags?: string[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  searchQuery?: string;
}

/**
 * Risk statistics for reporting
 */
export interface RiskStatistics {
  total: number;
  byLevel: Record<RiskLevel, number>;
  byCategory: Record<RiskCategory, number>;
  byStatus: Record<RiskStatus, number>;
  averageScore: number;
  topRisks: Risk[];
  recentlyIdentified: Risk[];
  overdueReviews: Risk[];
  trend: 'improving' | 'stable' | 'worsening';
}

/**
 * Risk assessment template
 */
export interface RiskTemplate {
  id: string;
  name: string;
  category: RiskCategory;
  description: string;
  typicalProbability: RiskProbability;
  typicalImpact: RiskImpact;
  commonMitigation: string;
  commonContingency: string;
}

/**
 * Predefined risk templates
 */
export const COMMON_RISK_TEMPLATES: RiskTemplate[] = [
  {
    id: 'tech-debt',
    name: 'Dette Technique',
    category: 'technical',
    description: 'Accumulation de dette technique ralentissant le développement',
    typicalProbability: 3,
    typicalImpact: 3,
    commonMitigation: 'Allouer du temps pour le refactoring régulier',
    commonContingency: 'Planifier un sprint dédié au nettoyage technique',
  },
  {
    id: 'key-person',
    name: 'Départ Personne Clé',
    category: 'organizational',
    description: 'Départ d\'un membre clé de l\'équipe',
    typicalProbability: 2,
    typicalImpact: 4,
    commonMitigation: 'Documentation et partage de connaissances',
    commonContingency: 'Plan de succession et backup des compétences',
  },
  {
    id: 'budget-overrun',
    name: 'Dépassement Budget',
    category: 'financial',
    description: 'Dépassement du budget alloué au projet',
    typicalProbability: 3,
    typicalImpact: 4,
    commonMitigation: 'Suivi rigoureux des dépenses et alertes précoces',
    commonContingency: 'Réduction du périmètre ou demande de budget additionnel',
  },
  {
    id: 'scope-creep',
    name: 'Extension du Périmètre',
    category: 'schedule',
    description: 'Ajouts non contrôlés de fonctionnalités',
    typicalProbability: 4,
    typicalImpact: 3,
    commonMitigation: 'Process strict de gestion des changements',
    commonContingency: 'Priorisation stricte et négociation des délais',
  },
  {
    id: 'dependency-delay',
    name: 'Retard Dépendance Externe',
    category: 'external',
    description: 'Retard d\'une dépendance externe (API, fournisseur)',
    typicalProbability: 3,
    typicalImpact: 4,
    commonMitigation: 'Identification précoce et suivi rapproché des dépendances',
    commonContingency: 'Solutions alternatives ou mocks temporaires',
  },
  {
    id: 'quality-issues',
    name: 'Problèmes de Qualité',
    category: 'quality',
    description: 'Défauts qualité nécessitant du retravail important',
    typicalProbability: 3,
    typicalImpact: 3,
    commonMitigation: 'Tests automatisés et revues de code systématiques',
    commonContingency: 'Sprint de correction et tests approfondis',
  },
];

/**
 * Risk heat map cell (for visualization)
 */
export interface RiskHeatMapCell {
  probability: RiskProbability;
  impact: RiskImpact;
  score: number;
  level: RiskLevel;
  risks: Risk[];
  count: number;
}
